import { Breadcrumb } from './useBreadcrumbs';
import { Routes } from '../../enums/Routes';
import i18n from '../../i18n/config';

type RouteParams = Record<string, string | undefined>;

const createBreadcrumb = (label: string, path: string): Breadcrumb => ({
  label,
  path,
});

export const getLastSegmentFromRoute = (route: string): string => {
  const segments = route.split('/').filter(Boolean);
  return segments[segments.length - 1];
};

export const getRoutePatternBeforeParams = (route: string): string => {
  const paramIndex = route.indexOf(':');
  return paramIndex > 0 ? route.substring(0, paramIndex) : route;
};

export const getSegmentAfterFirstParam = (route: string): string => {
  const segments = route.split('/').filter(Boolean);
  const paramIndex = segments.findIndex(seg => seg.startsWith(':'));
  return paramIndex >= 0 && segments[paramIndex + 1] && !segments[paramIndex + 1].startsWith(':')
    ? segments[paramIndex + 1]
    : segments[segments.length - 1];
};

export const buildDashboardBreadcrumbs = (
  pathname: string,
  params: RouteParams
): Breadcrumb[] => {
  const workEnvironmentBase = getRoutePatternBeforeParams(Routes.DashboardWorkEnvironmentSurvey);
  const isWorkEnvironmentSurvey =
    pathname.includes(workEnvironmentBase) && params.surveyId;

  if (!isWorkEnvironmentSurvey) {
    return [];
  }

  return [
    createBreadcrumb(i18n.t('breadcrumbs.overview'), Routes.Dashboard),
    createBreadcrumb(i18n.t('breadcrumbs.workEnvironment'), Routes.DashboardWorkEnvironment),
    createBreadcrumb(i18n.t('breadcrumbs.survey'), pathname),
  ];
};

/** The pages opened from the cards in HR's overview of the work environment */
const WORK_ENVIRONMENT_SUBPAGES = [
  { route: Routes.HRWorkEnvironmentMeasuresFullPath, page: 'measures' },
  { route: Routes.HRWorkEnvironmentMissingSafetyRepsFullPath, page: 'safetyReps' },
  { route: Routes.HRWorkEnvironmentSurveysDueFullPath, page: 'surveys' },
  { route: Routes.HRWorkEnvironmentRequirementsFullPath, page: 'requirements' },
  { route: Routes.HRWorkEnvironmentProcessFullPath, page: 'process' },
  { route: Routes.HRWorkEnvironmentRisksFullPath, page: 'risks' },
  { route: Routes.HRWorkEnvironmentTeamsFullPath, page: 'teams' },
];

const buildHRWorkEnvironmentBreadcrumbs = (
  pathname: string,
  params: RouteParams
): Breadcrumb[] | null => {
  const baseBreadcrumbs = [
    createBreadcrumb(i18n.t('breadcrumbs.hr'), Routes.HRDashboard),
    createBreadcrumb(i18n.t('breadcrumbs.workEnvironment'), Routes.HRWorkEnvironmentFullPath),
  ];

  const subpage = WORK_ENVIRONMENT_SUBPAGES.find(({ route }) => route === pathname);
  if (subpage) {
    return [...baseBreadcrumbs, createBreadcrumb(i18n.t(`hrOverview.pages.${subpage.page}.title`), pathname)];
  }

  const surveyBase = getRoutePatternBeforeParams(Routes.HRWorkEnvironmentSurveyDetailFullPath);
  if (pathname.includes(surveyBase) && params.surveyId) {
    return [...baseBreadcrumbs, createBreadcrumb(i18n.t('breadcrumbs.survey'), pathname)];
  }

  return null;
};

export const buildHRBreadcrumbs = (
  pathname: string,
  params: RouteParams
): Breadcrumb[] => {
  const workEnvironmentBreadcrumbs = buildHRWorkEnvironmentBreadcrumbs(
    pathname,
    params
  );
  if (workEnvironmentBreadcrumbs) return workEnvironmentBreadcrumbs;

  return [];
};

const buildTeamsAnalyzeSurveyBreadcrumbs = (
  pathname: string,
  teamName: string
): Breadcrumb[] => {
  const baseBreadcrumbs = [
    createBreadcrumb(i18n.t('breadcrumbs.myTeams'), Routes.Teams),
    createBreadcrumb(teamName, Routes.Teams),
  ];

  const summarySegment = getLastSegmentFromRoute(Routes.AnalyzeSurveySummary);
  const isSummary = pathname.includes(`/${summarySegment}`);

  if (isSummary) {
    return [
      ...baseBreadcrumbs,
      createBreadcrumb(i18n.t('breadcrumbs.analyzeSurvey'), pathname.replace(`/${summarySegment}`, '/0')),
      createBreadcrumb(i18n.t('breadcrumbs.summary'), pathname),
    ];
  }

  return [...baseBreadcrumbs, createBreadcrumb(i18n.t('breadcrumbs.analyzeSurvey'), pathname)];
};

export const buildTeamsBreadcrumbs = (
  pathname: string,
  params: RouteParams,
  getTeamName: (teamId: string) => string
): Breadcrumb[] => {
  if (!params.teamId) return [];

  const teamName = getTeamName(params.teamId);
  const baseBreadcrumbs = [createBreadcrumb(i18n.t('breadcrumbs.myTeams'), Routes.Teams)];

  const setupSegment = getSegmentAfterFirstParam(Routes.Team);
  if (pathname.includes(`/${setupSegment}`)) {
    return [...baseBreadcrumbs, createBreadcrumb(teamName, pathname)];
  }

  const accessManagementSegment = getSegmentAfterFirstParam(Routes.TeamAccessManagement);
  if (pathname.includes(`/${accessManagementSegment}`)) {
    return [
      ...baseBreadcrumbs,
      createBreadcrumb(teamName, Routes.Team.replace(':teamId', params.teamId)),
      createBreadcrumb(i18n.t('teamSettings.tabs.accessManagement'), pathname),
    ];
  }

  const integrationSegment = getSegmentAfterFirstParam(Routes.TeamIntegration);
  if (pathname.includes(`/${integrationSegment}`)) {
    return [
      ...baseBreadcrumbs,
      createBreadcrumb(teamName, Routes.Team.replace(':teamId', params.teamId)),
      createBreadcrumb(i18n.t('teamSettings.tabs.distributionChannels'), pathname),
    ];
  }

  const schedulingSegment = getSegmentAfterFirstParam(Routes.TeamScheduling);
  if (pathname.includes(`/${schedulingSegment}`)) {
    return [
      ...baseBreadcrumbs,
      createBreadcrumb(teamName, Routes.Team.replace(':teamId', params.teamId)),
      createBreadcrumb(i18n.t('teamSettings.tabs.scheduling'), pathname),
    ];
  }

  const analyzeSurveySegment = getSegmentAfterFirstParam(Routes.AnalyzeSurvey);
  if (pathname.includes(`/${analyzeSurveySegment}`)) {
    return buildTeamsAnalyzeSurveyBreadcrumbs(pathname, teamName);
  }

  const questionTrendsSegment = getSegmentAfterFirstParam(Routes.QuestionTrends);
  if (pathname.includes(`/${questionTrendsSegment}`)) {
    return [
      ...baseBreadcrumbs,
      createBreadcrumb(teamName, Routes.Teams),
      createBreadcrumb(i18n.t('breadcrumbs.questionTrends'), pathname),
    ];
  }

  return [];
};

const buildOrganizationTeamsBreadcrumbs = (
  pathname: string,
  params: RouteParams,
  getTeamName: (teamId: string) => string
): Breadcrumb[] | null => {
  const baseBreadcrumbs = [
    createBreadcrumb(i18n.t('breadcrumbs.organization'), Routes.OrganizationTeamsOverview),
    createBreadcrumb(i18n.t('breadcrumbs.team'), Routes.OrganizationTeamsOverview),
  ];

  const createTeamSegment = getLastSegmentFromRoute(Routes.OrganizationCreateTeam);
  if (pathname.includes(`/${createTeamSegment}`)) {
    return [...baseBreadcrumbs, createBreadcrumb(i18n.t('breadcrumbs.createTeam'), pathname)];
  }

  const schedulingSegment = getSegmentAfterFirstParam(Routes.OrganizationTeamScheduling);
  if (pathname.includes(`/${schedulingSegment}`) && params.teamId) {
    const teamName = getTeamName(params.teamId);
    const teamPath = Routes.OrganizationTeam.replace(':teamId', params.teamId);
    return [
      ...baseBreadcrumbs,
      createBreadcrumb(teamName, teamPath),
      createBreadcrumb(i18n.t('teamSettings.tabs.scheduling'), pathname),
    ];
  }

  const teamsBase = getRoutePatternBeforeParams(Routes.OrganizationTeamsOverview);
  if (pathname.includes(teamsBase) && params.teamId) {
    const teamName = getTeamName(params.teamId);
    return [...baseBreadcrumbs, createBreadcrumb(teamName, pathname)];
  }

  return null;
};

const buildOrganizationUsersBreadcrumbs = (pathname: string): Breadcrumb[] | null => {
  const baseBreadcrumbs = [
    createBreadcrumb(i18n.t('breadcrumbs.organization'), Routes.OrganizationTeamsOverview),
    createBreadcrumb(i18n.t('breadcrumbs.users'), Routes.OrganizationUsersOverviewPage),
  ];

  const userRouteMap = {
    [getRoutePatternBeforeParams(Routes.OrganizationInviteUser)]: i18n.t('breadcrumbs.inviteUser'),
    [getRoutePatternBeforeParams(Routes.OrganizationViewUser)]: i18n.t('breadcrumbs.viewUser'),
    [getRoutePatternBeforeParams(Routes.OrganizationEditUser)]: i18n.t('breadcrumbs.editUser'),
  } as const;

  const matchedRoute = Object.keys(userRouteMap).find((route) =>
    pathname.includes(route)
  );

  if (!matchedRoute) return null;

  return [...baseBreadcrumbs, createBreadcrumb(userRouteMap[matchedRoute as keyof typeof userRouteMap], pathname)];
};

const buildOrganizationSubscriptionsBreadcrumbs = (pathname: string): Breadcrumb[] | null => {
  const subscriptionsBase = Routes.OrganizationSubscriptions;
  if (!pathname.includes(subscriptionsBase)) {
    return null;
  }

  // No breadcrumbs for the main subscriptions page (consistent with other org pages)
  return null;
};

export const buildOrganizationBreadcrumbs = (
  pathname: string,
  params: RouteParams,
  getTeamName: (teamId: string) => string
): Breadcrumb[] => {
  const teamsBreadcrumbs = buildOrganizationTeamsBreadcrumbs(
    pathname,
    params,
    getTeamName
  );
  if (teamsBreadcrumbs) return teamsBreadcrumbs;

  const usersBreadcrumbs = buildOrganizationUsersBreadcrumbs(pathname);
  if (usersBreadcrumbs) return usersBreadcrumbs;

  const subscriptionsBreadcrumbs = buildOrganizationSubscriptionsBreadcrumbs(pathname);
  if (subscriptionsBreadcrumbs) return subscriptionsBreadcrumbs;

  return [];
};

export const buildContentSystemBreadcrumbs = (
  pathname: string,
  params: RouteParams
): Breadcrumb[] => {
  const baseBreadcrumbs = [
    createBreadcrumb(i18n.t('breadcrumbs.contentSystem'), Routes.ContentManagementSystemArticles),
    createBreadcrumb(i18n.t('breadcrumbs.articles'), Routes.ContentManagementSystemArticles),
  ];

  const articleRouteMap = {
    [getRoutePatternBeforeParams(Routes.ContentManagementSystemCreateArticle)]: i18n.t('breadcrumbs.createArticle'),
    [getRoutePatternBeforeParams(Routes.ContentManagementSystemEditArticle)]: i18n.t('breadcrumbs.editArticle'),
  } as const;

  const matchedRoute = Object.keys(articleRouteMap).find((route) =>
    pathname.includes(route)
  );

  if (matchedRoute) {
    return [...baseBreadcrumbs, createBreadcrumb(articleRouteMap[matchedRoute as keyof typeof articleRouteMap], pathname)];
  }

  const articlesBase = getRoutePatternBeforeParams(Routes.ContentManagementSystemArticles);
  if (pathname.includes(articlesBase) && params.id) {
    return [...baseBreadcrumbs, createBreadcrumb(i18n.t('breadcrumbs.article'), pathname)];
  }

  return [];
};

const PATH_SEGMENTS = {
  RESPONSE: '/response/',
  SURVEY: '/survey/',
} as const;

const buildSurveyPath = (templateId: string, surveyId: string): string => {
  return Routes.DashboardCustomSurveySurvey
    .replace(':templateId', templateId)
    .replace(':surveyId', surveyId);
};

const addTemplateBreadcrumb = (
  breadcrumbs: Breadcrumb[],
  pathname: string,
  params: RouteParams
): void => {
  const templateBase = getRoutePatternBeforeParams(Routes.DashboardCustomSurveyTemplateTrends);
  if (!pathname.includes(templateBase) || !params.templateId) return;

  const templatePath = Routes.DashboardCustomSurveyTemplateTrends.replace(':templateId', params.templateId);
  breadcrumbs.push(createBreadcrumb(i18n.t('breadcrumbs.template'), templatePath));
};

const addSurveyBreadcrumb = (
  breadcrumbs: Breadcrumb[],
  pathname: string,
  params: RouteParams
): void => {
  if (!params.templateId || !params.surveyId) return;

  const surveyPath = buildSurveyPath(params.templateId, params.surveyId);
  breadcrumbs.push(createBreadcrumb(i18n.t('breadcrumbs.survey'), surveyPath));
};

const addResponseDetailBreadcrumbs = (
  breadcrumbs: Breadcrumb[],
  params: RouteParams
): void => {
  if (!params.templateId || !params.surveyId) return;

  const surveyPath = buildSurveyPath(params.templateId, params.surveyId);
  breadcrumbs.push(createBreadcrumb(i18n.t('breadcrumbs.survey'), surveyPath));
  breadcrumbs.push(createBreadcrumb(`${i18n.t('breadcrumbs.response')} #${params.responseId}`, ''));
};

export const buildCustomSurveyBreadcrumbs = (
  pathname: string,
  params: RouteParams
): Breadcrumb[] => {
  const breadcrumbs: Breadcrumb[] = [
    createBreadcrumb(i18n.t('breadcrumbs.overview'), Routes.DashboardCustomSurveys),
  ];

  addTemplateBreadcrumb(breadcrumbs, pathname, params);

  if (!params.surveyId) return breadcrumbs;

  const isResponseDetail = pathname.includes(PATH_SEGMENTS.RESPONSE) && params.responseId;

  if (isResponseDetail) {
    addResponseDetailBreadcrumbs(breadcrumbs, params);
  } else {
    // Survey page (with tabs for aggregated/individual)
    addSurveyBreadcrumb(breadcrumbs, pathname, params);
  }

  return breadcrumbs;
};
